import type { CogoXyzTarget } from '@cogo/types-v2'
import { cogoXyzRegistry } from '@/features/cogo-xyz/cogo-xyz-registry'
import { saveCogoXyzRegistryToCurrentProject } from '@/features/cogo-xyz/cogo-xyz-project-storage'
import type { DesignSystemState } from '@/features/design-system'
import type { AppSpecFull } from '@/services/block-api-service'
import { slugDesignSystemId } from '@/features/design-system/lib/design-system-export'
import type {
  StudioDecomposePlanningMetadata,
  StudioProjectPlan,
} from '@/features/project/lib/studio-project-plan'

export interface RegisterChatStartedAppInLibraryOptions {
  projectId: string
  prompt: string
  appSpec: AppSpecFull
  studioProjectPlan: StudioProjectPlan
  planningMetadata?: StudioDecomposePlanningMetadata
  designSystemPreset?: DesignSystemState | null
  createdBy?: string
  persistToProject?: boolean
}

function currentOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '')
  }
  return 'https://cogo.xyz'
}

function projectPreviewUrl(projectId: string): string {
  return `${currentOrigin()}/ide/${encodeURIComponent(projectId)}`
}

function searchableAppText(appSpec: AppSpecFull): string {
  return [
    appSpec.name,
    appSpec.description,
    appSpec.appClass,
    appSpec.targetPlatform,
    appSpec.generationTargetPlatform,
    ...(appSpec.pages ?? []).flatMap((page) => [page.title, page.route, ...page.blocks]),
    ...appSpec.blocks.flatMap((block) => [block.name, block.type, block.description]),
  ].filter(Boolean).join(' ').toLowerCase()
}

export function libraryTargetFromAppSpec(appSpec: AppSpecFull): CogoXyzTarget {
  const text = searchableAppText(appSpec)
  if (/\b(dashboard|analytics|report|metric|kpi|chart|finance)\b/.test(text)) return 'dashboard'
  if (/\b(admin|crm|ops|operation|inventory|backoffice|internal)\b/.test(text)) return 'admin'
  if (/\b(creator|influencer|fan|profile|link in bio|portfolio|event page)\b/.test(text)) return 'creator_page'
  if (/\b(landing|homepage|campaign|marketing|launch page)\b/.test(text)) return 'landing_page'
  return 'app'
}

function hasLibraryItemForProject(projectId: string): boolean {
  return cogoXyzRegistry.listLibraryItems({ includeArchived: true }).items.some((item) => {
    const metadata = item.workItem.sourceRef.metadata
    return metadata && metadata.projectId === projectId
  })
}

function designSystemIdForRegistration(options: RegisterChatStartedAppInLibraryOptions): string | undefined {
  return options.studioProjectPlan.designSystemId ??
    (options.designSystemPreset ? slugDesignSystemId(options.designSystemPreset) : undefined)
}

export async function registerChatStartedAppInLibrary(
  options: RegisterChatStartedAppInLibraryOptions,
): Promise<void> {
  cogoXyzRegistry.reset()
  if (hasLibraryItemForProject(options.projectId)) return
  const designSystemId = designSystemIdForRegistration(options)
  const planningSource =
    options.planningMetadata?.planningSource ?? options.appSpec.planningSource
  const decomposePlanning =
    options.planningMetadata ?? options.appSpec.decomposePlanning
  const registrationMetadata = {
    entryPoint: 'studio_chat',
    agenticRagBacked: true,
    generationTool: 'cogo.studio.requestAppGeneration',
    projectId: options.projectId,
    appId: options.appSpec.appId,
    ...(options.appSpec.appClass === undefined
      ? {}
      : { appClass: options.appSpec.appClass }),
    ...(options.appSpec.targetPlatform === undefined
      ? {}
      : { targetPlatform: options.appSpec.targetPlatform }),
    ...(options.appSpec.generationTargetPlatform === undefined
      ? {}
      : { generationTargetPlatform: options.appSpec.generationTargetPlatform }),
    runtimeTargets:
      options.appSpec.runtimeTargets ?? options.studioProjectPlan.runtimeTargets,
    ...(designSystemId === undefined ? {} : { designSystemId }),
    ...(options.studioProjectPlan.securityProfile === undefined
      ? {}
      : { securityProfile: options.studioProjectPlan.securityProfile }),
    ...(planningSource === undefined ? {} : { planningSource }),
    ...(decomposePlanning === undefined ? {} : { decomposePlanning }),
    ...(options.appSpec.interactionContract === undefined
      ? {}
      : { interactionContract: options.appSpec.interactionContract }),
  }

  const created = cogoXyzRegistry.createCogoApp({
    workspaceId: options.projectId,
    title: options.appSpec.name,
    sourceType: 'assistant_prompt',
    inputType: 'prompt',
    input: options.prompt,
    target: libraryTargetFromAppSpec(options.appSpec),
    ...(options.createdBy === undefined ? {} : { createdBy: options.createdBy }),
  }, {
    bundleRef: `studio://projects/${options.projectId}/chat-generation`,
    previewUrl: projectPreviewUrl(options.projectId),
    toolName: 'chat_start_app_generation',
    metadata: registrationMetadata,
  })
  created.workItem.sourceRef = {
    ...created.workItem.sourceRef,
    metadata: {
      ...created.workItem.sourceRef.metadata,
      ...registrationMetadata,
    },
  }

  if (options.persistToProject !== false) await persistChatLibraryRegistryToCurrentProject()
}

export async function persistChatLibraryRegistryToCurrentProject(): Promise<void> {
  try {
    await saveCogoXyzRegistryToCurrentProject()
  } catch (err) {
    console.warn('[chat-library] Failed to persist chat-started Library item', err)
  }
}
